import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DrawIcon from "@mui/icons-material/Draw";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import EditIcon from "@mui/icons-material/Edit";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DirectionsChip from "../../../components/DirectionsChip";
import SignaturePad from "signature_pad";
import { fireConfetti } from "../../../utils/fireConfetti";
import { useNavigate, useParams } from "react-router-dom";
import { hasLocallyValidSession, fetchCurrentUserProfile } from "../../../services/auth/auth-session";
import { updateMyProfile } from "../../../services/auth/profile-api";
import PhoneInput from "../../../components/PhoneInput";
import { isValidE164Phone } from "../../../utils/phone-utils";
import LoginDialog from "../../auth/login-dialog";
import { getLeague, getLeagueGames } from "../../../services/api/leagues";
import { registerForGame } from "../../../services/api/games";
import { newUuid } from "../../../utils/uuid";
import { getLeagueWaivers, getWaiver, getMyWaiverSignature, resetMyWaiverSignature, signWaiver, getSignaturePdfUploadUrl, getSignatureImageUploadUrl, storeSignatureKeys } from "../../../services/api/waivers";
import { SKILL_LEVEL_LABELS, formatGameTime, formatCost } from "../../../components/GameCard";
import { loadErrorMessage } from "../../../utils/api-error";

export default function LeagueSignupView() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [league, setLeague] = React.useState(null);
  const [games, setGames] = React.useState([]);
  const [loadError, setLoadError] = React.useState("");
  const [loading, setLoading] = React.useState(hasLocallyValidSession());

  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [role, setRole] = React.useState("SKATER");
  const [preferredPosition, setPreferredPosition] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [selectedGameIds, setSelectedGameIds] = React.useState([]);
  const [submitState, setSubmitState] = React.useState({ loading: false, error: "", successCount: 0 });
  const [paymentInfo, setPaymentInfo] = React.useState(null);
  const [paymentConfirmed, setPaymentConfirmed] = React.useState(false);
  const [paymentReporting, setPaymentReporting] = React.useState(false);
  const [venmoClicked, setVenmoClicked] = React.useState(false);
  const [successModalOpen, setSuccessModalOpen] = React.useState(false);

  const [gamePage, setGamePage] = React.useState(1);
  const [gamesPerPage, setGamesPerPage] = React.useState(5);
  const [authPromptOpen, setAuthPromptOpen] = React.useState(!hasLocallyValidSession());
  const [fetchReady, setFetchReady] = React.useState(hasLocallyValidSession());
  const [loginOpen, setLoginOpen] = React.useState(false);
  const [profileLocked, setProfileLocked] = React.useState(false);
  const [phoneLockedFromProfile, setPhoneLockedFromProfile] = React.useState(false);

  const [profileModalOpen, setProfileModalOpen] = React.useState(false);
  const [profileModalForm, setProfileModalForm] = React.useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [profileModalSaving, setProfileModalSaving] = React.useState(false);
  const [profileModalError, setProfileModalError] = React.useState("");

  const [waivers, setWaivers] = React.useState([]);
  const [waiverSignedIds, setWaiverSignedIds] = React.useState(new Set());

  // Waiver dialog state
  const [waiverDialogOpen, setWaiverDialogOpen] = React.useState(false);
  const [waiverDialogWaiver, setWaiverDialogWaiver] = React.useState(null);
  const [waiverDialogContent, setWaiverDialogContent] = React.useState("");
  const [waiverDialogLoading, setWaiverDialogLoading] = React.useState(false);
  const [waiverDialogFullName, setWaiverDialogFullName] = React.useState("");
  const [waiverDialogAccepted, setWaiverDialogAccepted] = React.useState(false);
  const [waiverDialogSubmitting, setWaiverDialogSubmitting] = React.useState(false);
  const [waiverDialogError, setWaiverDialogError] = React.useState("");
  const [waiverDialogTab, setWaiverDialogTab] = React.useState(0);
  const [waiverDialogHasDrawn, setWaiverDialogHasDrawn] = React.useState(false);
  const waiverDialogCanvasRef = React.useRef(null);
  const waiverDialogSigPadRef = React.useRef(null);
  const waiverDialogContentRef = React.useRef(null);

  React.useEffect(() => {
    if (!leagueId || !fetchReady) return;
    setLoading(true);
    const fetches = [getLeague(leagueId), getLeagueGames(leagueId), getLeagueWaivers(leagueId)];
    if (hasLocallyValidSession()) {
      fetches.push(fetchCurrentUserProfile());
    }
    Promise.all(fetches)
      .then(([leagueData, gamesList, waiverList, profile]) => {
        setLeague(leagueData);
        const upcoming = (Array.isArray(gamesList) ? gamesList : []).filter(
          (g) => new Date(g.startsAt) >= new Date()
        );
        setGames(upcoming);
        setSelectedGameIds(upcoming.filter((g) => !g.currentUserRole).map((g) => g.id));
        setWaivers(Array.isArray(waiverList) ? waiverList : []);
        if (profile?.firstName || profile?.lastName || profile?.email) {
          setFirstName(profile.firstName ?? "");
          setLastName(profile.lastName ?? "");
          setEmail(profile.email ?? "");
          setPhone(profile.phone ?? "");
          setPhoneLockedFromProfile(!!profile.phone);
          setProfileLocked(true);
        }
      })
      .catch((err) => {
        const msg = err?.message ?? "";
        if (msg.includes("private") || msg.includes("Contact the organizer")) {
          setLoadError("This league is private. Contact the organizer to request access.");
        } else {
          setLoadError(loadErrorMessage(err, "Failed to load league details."));
        }
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, fetchReady]);

  // When email is entered, check signature status for all waivers
  const checkWaiverSignatures = React.useCallback(async (emailValue) => {
    if (!emailValue || waivers.length === 0) return;
    const results = await Promise.allSettled(
      waivers.map((w) => getMyWaiverSignature(leagueId, w.id, emailValue))
    );
    const signedSet = new Set();
    results.forEach((r, i) => {
      if (r.status === "fulfilled" && r.value?.signed) {
        signedSet.add(waivers[i].id);
      }
    });
    setWaiverSignedIds(signedSet);
  }, [leagueId, waivers]);

  // Re-fetch profile + games when the user signs in from anywhere (navbar or banner)
  React.useEffect(() => {
    const handler = () => {
      if (!hasLocallyValidSession() || !leagueId) return;
      Promise.all([fetchCurrentUserProfile(), getLeagueGames(leagueId)])
        .then(([profile, gamesList]) => {
          if (profile?.firstName || profile?.lastName || profile?.email) {
            setFirstName(profile.firstName ?? "");
            setLastName(profile.lastName ?? "");
            setEmail(profile.email ?? "");
            setPhone(profile.phone ?? "");
            setPhoneLockedFromProfile(!!profile.phone);
            setProfileLocked(true);
            if (profile.email) checkWaiverSignatures(profile.email);
          }
          const upcoming = (Array.isArray(gamesList) ? gamesList : []).filter(
            (g) => new Date(g.startsAt) >= new Date()
          );
          setGames(upcoming);
          setSelectedGameIds(upcoming.filter((g) => !g.currentUserRole).map((g) => g.id));
        });
    };
    window.addEventListener("puck-pickup-auth-change", handler);
    return () => window.removeEventListener("puck-pickup-auth-change", handler);
  }, [leagueId, checkWaiverSignatures]);

  // When profile is pre-filled and waivers load, auto-check signature status
  React.useEffect(() => {
    if (profileLocked && email && waivers.length > 0) {
      checkWaiverSignatures(email);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLocked, waivers]);

  // Initialize SignaturePad when draw tab is selected in waiver dialog
  React.useEffect(() => {
    if (!waiverDialogOpen || waiverDialogTab !== 1 || !waiverDialogCanvasRef.current) return;

    const canvas = waiverDialogCanvasRef.current;
    const pad = new SignaturePad(canvas, {
      backgroundColor: isDark ? "rgb(30, 30, 30)" : "rgb(255, 255, 255)",
      penColor: isDark ? "rgb(255, 255, 255)" : "rgb(0, 0, 0)",
    });
    waiverDialogSigPadRef.current = pad;
    pad.addEventListener("endStroke", () => setWaiverDialogHasDrawn(!pad.isEmpty()));

    const resizeCanvas = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const data = pad.toData();
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d").scale(ratio, ratio);
      pad.clear();
      if (data.length > 0) pad.fromData(data);
    };
    resizeCanvas();
    const ro = new ResizeObserver(resizeCanvas);
    ro.observe(canvas);

    return () => {
      ro.disconnect();
      pad.off();
      waiverDialogSigPadRef.current = null;
    };
  }, [waiverDialogOpen, waiverDialogTab, isDark]);

  const getWaiverDialogSignatureDataUrl = () => {
    if (waiverDialogTab === 1 && waiverDialogSigPadRef.current && !waiverDialogSigPadRef.current.isEmpty()) {
      return waiverDialogSigPadRef.current.toDataURL("image/png");
    }
    return null;
  };

  const handleClearWaiverDialogSignature = () => {
    waiverDialogSigPadRef.current?.clear();
    setWaiverDialogHasDrawn(false);
  };


  const uploadWaiverArtifactsToS3 = async (signatureId, waiver, contentHtml, signerName, sigDataUrl) => {
    const keysToStore = {};

    // Upload the raw PNG signature image first — fast and most important
    if (sigDataUrl) {
      try {
        const imgResult = await getSignatureImageUploadUrl(leagueId, waiver.id, signatureId);
        if (imgResult?.configured !== false) {
          const blob = await fetch(sigDataUrl).then((r) => r.blob());
          await fetch(imgResult.uploadUrl, { method: "PUT", headers: { "Content-Type": "image/png" }, body: blob });
          keysToStore.imageS3Key = imgResult.imageS3Key;
        }
      } catch {
        // Best-effort
      }
    }

    // Upload the full PDF (waiver text + signature block)
    try {
      const pdfResult = await getSignaturePdfUploadUrl(leagueId, waiver.id, signatureId);
      if (pdfResult?.configured === false) throw new Error("S3 not configured");
      const { uploadUrl, s3Key } = pdfResult;
      const html2pdf = (await import("html2pdf.js")).default;
      const signedAt = new Date().toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" });
      const signatureBlock = sigDataUrl
        ? `<img src="${sigDataUrl}" style="max-width:260px;border-bottom:1px solid #333;display:block;" />`
        : `<p style="font-family:'Brush Script MT',cursive;font-size:28px;margin:0;">${signerName}</p>`;
      const el = document.createElement("div");
      el.innerHTML = `
        <div style="font-family:Arial,sans-serif;font-size:13px;line-height:1.6;color:#111;max-width:800px;margin:0 auto;padding:40px;">
          <h1 style="font-size:20px;margin-bottom:4px;">${waiver.name}</h1>
          <p style="color:#666;font-size:12px;margin-top:0;">Version ${waiver.version}</p>
          <hr style="border:none;border-top:1px solid #ddd;margin:16px 0;" />
          ${contentHtml}
          <hr style="border:none;border-top:1px solid #ddd;margin:32px 0 16px;" />
          <h3 style="font-size:14px;margin-bottom:8px;">Signature</h3>
          ${signatureBlock}
          <p style="margin-top:8px;font-size:12px;color:#555;">
            Signed by: <strong>${signerName}</strong><br/>
            Email: ${email || "N/A"}<br/>
            Date: ${signedAt}
          </p>
          <p style="font-size:11px;color:#888;margin-top:24px;">
            This document was signed electronically in accordance with the E-SIGN Act and UETA.
          </p>
        </div>`;
      document.body.appendChild(el);
      const pdfBlob = await html2pdf()
        .set({ margin: 0, filename: "waiver.pdf", image: { type: "jpeg", quality: 0.98 }, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: "in", format: "letter", orientation: "portrait" } })
        .from(el)
        .outputPdf("blob");
      document.body.removeChild(el);
      await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": "application/pdf" }, body: pdfBlob });
      keysToStore.s3Key = s3Key;
    } catch {
      // Best-effort
    }

    if (Object.keys(keysToStore).length > 0) {
      try {
        await storeSignatureKeys(leagueId, waiver.id, signatureId, keysToStore);
      } catch {
        // Best-effort
      }
    }
  };

  const [resetingWaiverIds, setResetingWaiverIds] = React.useState(new Set());

  const handleResetWaiver = async (waiverId) => {
    setResetingWaiverIds((prev) => new Set([...prev, waiverId]));
    try {
      await resetMyWaiverSignature(leagueId, waiverId, email.trim().toLowerCase());
      setWaiverSignedIds((prev) => {
        const next = new Set(prev);
        next.delete(waiverId);
        return next;
      });
    } catch {
      // non-fatal — signature status unchanged
    } finally {
      setResetingWaiverIds((prev) => {
        const next = new Set(prev);
        next.delete(waiverId);
        return next;
      });
    }
  };

  const unsignedWaivers = waivers.filter((w) => !waiverSignedIds.has(w.id));

  const handleOpenProfileModal = () => {
    setProfileModalForm({ firstName, lastName, email, phone });
    setProfileModalError("");
    setProfileModalOpen(true);
  };

  const handleSaveProfile = async () => {
    if (!profileModalForm.firstName.trim() || !profileModalForm.lastName.trim()) {
      setProfileModalError("First and last name are required.");
      return;
    }
    if (!isValidE164Phone(profileModalForm.phone)) {
      setProfileModalError("Please enter a valid phone number.");
      return;
    }
    setProfileModalSaving(true);
    setProfileModalError("");
    try {
      await updateMyProfile({
        firstName: profileModalForm.firstName.trim(),
        lastName: profileModalForm.lastName.trim(),
        email: profileModalForm.email.trim().toLowerCase(),
        phone: profileModalForm.phone,
      });
      setFirstName(profileModalForm.firstName.trim());
      setLastName(profileModalForm.lastName.trim());
      setEmail(profileModalForm.email.trim().toLowerCase());
      setPhone(profileModalForm.phone);
      setPhoneLockedFromProfile(!!profileModalForm.phone);
      setProfileModalOpen(false);
    } catch (err) {
      setProfileModalError(err?.message || "Failed to save. Please try again.");
    } finally {
      setProfileModalSaving(false);
    }
  };

  const handleOpenWaiverDialog = async (waiver) => {
    if (!hasLocallyValidSession()) {
      setLoginOpen(true);
      return;
    }
    setWaiverDialogWaiver(waiver);
    setWaiverDialogFullName([firstName, lastName].filter(Boolean).join(" "));
    setWaiverDialogAccepted(false);
    setWaiverDialogError("");
    setWaiverDialogContent("");
    setWaiverDialogTab(0);
    setWaiverDialogHasDrawn(false);
    setWaiverDialogOpen(true);
    setWaiverDialogLoading(true);
    try {
      const full = await getWaiver(leagueId, waiver.id);
      setWaiverDialogContent(full.contentHtml ?? "");
    } catch {
      // non-fatal — user can still sign without content visible
    } finally {
      setWaiverDialogLoading(false);
    }
  };

  const handleCloseWaiverDialog = () => {
    if (waiverDialogSubmitting) return;
    waiverDialogSigPadRef.current?.off();
    waiverDialogSigPadRef.current = null;
    setWaiverDialogOpen(false);
    setWaiverDialogWaiver(null);
    setWaiverDialogContent("");
    setWaiverDialogError("");
    setWaiverDialogTab(0);
    setWaiverDialogHasDrawn(false);
  };

  const handleSignWaiver = async () => {
    if (!waiverDialogWaiver || !waiverDialogAccepted) return;
    if (waiverDialogTab === 0 && !waiverDialogFullName.trim()) return;
    if (waiverDialogTab === 1 && !waiverDialogHasDrawn) return;
    setWaiverDialogSubmitting(true);
    setWaiverDialogError("");
    try {
      const sigDataUrl = getWaiverDialogSignatureDataUrl();
      const result = await signWaiver(leagueId, waiverDialogWaiver.id, {
        acceptedCheckbox: true,
        typedFullName: waiverDialogFullName.trim() || [firstName, lastName].filter(Boolean).join(" "),
        ...(sigDataUrl ? { signatureImage: sigDataUrl } : {}),
        email: email.trim().toLowerCase(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      const newSignatureId = result?.id ?? null;
      setWaiverSignedIds((prev) => new Set([...prev, waiverDialogWaiver.id]));
      // Capture values before dialog close resets state
      const waiverForPdf = waiverDialogWaiver;
      const contentForPdf = waiverDialogContent;
      const signerName = waiverDialogFullName.trim() || [firstName, lastName].filter(Boolean).join(" ");
      handleCloseWaiverDialog();
      if (newSignatureId && !result?.alreadySigned) {
        uploadWaiverArtifactsToS3(newSignatureId, waiverForPdf, contentForPdf, signerName, sigDataUrl).catch(() => {});
      }
    } catch (err) {
      setWaiverDialogError(err instanceof Error ? err.message : "Failed to sign waiver. Please try again.");
    } finally {
      setWaiverDialogSubmitting(false);
    }
  };

  const toggleGame = (gameId) => {
    setSelectedGameIds((prev) =>
      prev.includes(gameId) ? prev.filter((id) => id !== gameId) : [...prev, gameId]
    );
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!hasLocallyValidSession()) {
      setLoginOpen(true);
      return;
    }
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setSubmitState({ loading: false, error: "Please fill in all required fields.", successCount: 0 });
      return;
    }
    if (!isValidE164Phone(phone)) {
      setSubmitState({ loading: false, error: "Please enter a valid phone number.", successCount: 0 });
      return;
    }
    if (selectedGameIds.length === 0) {
      setSubmitState({ loading: false, error: "Please select at least one game to join.", successCount: 0 });
      return;
    }

    const costCents = role === "GOALIE" ? league.goalieCost : league.playerCost;
    const isFullLeaguePayment = league.gamePaymentType === "FULL_LEAGUE";
    const totalCents = isFullLeaguePayment ? costCents : selectedGameIds.length * costCents;

    if (totalCents > 0 && league.payoutMethod && league.payoutMethod !== "none") {
      // Show payment page first — registration happens only after the user confirms payment
      let venmoUrl = null;
      if (league.payoutMethod === "venmo" && league.venmoHandle) {
        const handle = league.venmoHandle.replace(/^@/, "");
        const amount = (totalCents / 100).toFixed(2);
        const note = `${new Date().getFullYear()} - ${league.name}`;
        venmoUrl = `https://venmo.com?recipients=${encodeURIComponent(handle)}&txn=pay&amount=${amount}&note=${encodeURIComponent(note)}`;
      }
      setPaymentInfo({ amountCents: totalCents, perGameCents: costCents, method: league.payoutMethod, venmoUrl, successCount: selectedGameIds.length, isFullLeague: isFullLeaguePayment, warningMsg: "", registrationIds: [] });
      return;
    }

    // Free or no payout method — register immediately and show success
    setSubmitState({ loading: true, error: "", successCount: 0 });
    let successCount = 0;
    let alreadyRegisteredCount = 0;
    let otherErrorCount = 0;
    const total = selectedGameIds.length;
    // One group id for the whole signup, so the player gets a single confirmation email.
    const signupGroupId = newUuid();
    for (let i = 0; i < selectedGameIds.length; i++) {
      const gameId = selectedGameIds[i];
      try {
        await registerForGame(gameId, {
          email: email.trim().toLowerCase(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          role,
          preferredPosition: role === "SKATER" && preferredPosition ? preferredPosition : undefined,
          signupGroupId,
          finalize: i === selectedGameIds.length - 1,
        });
        successCount++;
      } catch (err) {
        const msg = err instanceof Error ? err.message.toLowerCase() : "";
        if (msg.includes("already registered")) alreadyRegisteredCount++;
        else otherErrorCount++;
      }
    }

    if (successCount > 0) {
      if (hasLocallyValidSession()) {
        updateMyProfile({ firstName: firstName.trim(), lastName: lastName.trim(), phone: phone }).catch(() => {});
      }
      const parts = [];
      if (alreadyRegisteredCount > 0) {
        parts.push(`you are already registered for ${alreadyRegisteredCount} of the ${total} game${total !== 1 ? "s" : ""} selected`);
      }
      if (otherErrorCount > 0) {
        parts.push(`${otherErrorCount} registration${otherErrorCount !== 1 ? "s" : ""} failed`);
      }
      const warningMsg = parts.length > 0 ? `Registered for ${successCount} game${successCount !== 1 ? "s" : ""}. Note: ${parts.join("; ")}.` : "";
      fireConfetti();
      setSuccessModalOpen(true);
      setSubmitState({ loading: false, error: warningMsg, successCount: 0 });
    } else if (alreadyRegisteredCount === total) {
      setSubmitState({ loading: false, error: `You are already registered for all ${total} game${total !== 1 ? "s" : ""} selected.`, successCount: 0 });
    } else {
      setSubmitState({ loading: false, error: "Registration failed. Please try again.", successCount: 0 });
    }
  };

  if (!fetchReady) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <LoginDialog
          open={loginOpen}
          onClose={() => setLoginOpen(false)}
          onSuccess={() => { setFetchReady(true); setAuthPromptOpen(false); }}
        />
        <Dialog
          open={authPromptOpen}
          onClose={() => { setAuthPromptOpen(false); setFetchReady(true); }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", textAlign: "center", margin: "0 auto", alignItems: "center", gap: 1 }}>
            <LockOutlinedIcon color="primary" />
            Create an account to join
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ textAlign: "center" }} color="text.secondary" gutterBottom>
              You'll need a free Puck-Pickup account to sign up for this league. It only takes a minute!
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, flexDirection: "column", gap: 1 }}>
            <Button variant="contained" fullWidth onClick={() => { setAuthPromptOpen(false); setLoginOpen(true); }}>
              Create account / Sign in
            </Button>
            <Button variant="text" fullWidth onClick={() => { setAuthPromptOpen(false); setFetchReady(true); }}>
              Browse without signing in
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography color="text.secondary">Loading…</Typography>
      </Container>
    );
  }

  if (loadError || !league) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{loadError || "League not found."}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/search")}>Back to search</Button>
      </Container>
    );
  }

  if (paymentInfo) {
    const amountDollars = (paymentInfo.amountCents / 100).toFixed(2);

    const handleConfirmPayment = async () => {
      setPaymentReporting(true);

      // Register for each selected game now that the user has confirmed payment
      // The payment is sent with the registration so the server creates both
      // atomically. Full-league: one payment attached to the first selected game.
      // The server also records a missing payment for an already-registered game,
      // so retries after a partial failure recover cleanly.
      const note = `${new Date().getFullYear()}-${league.name}`;
      let registeredCount = 0;
      const signupGroupId = newUuid();
      for (let i = 0; i < selectedGameIds.length; i++) {
        const gameId = selectedGameIds[i];
        let payment;
        if (paymentInfo.isFullLeague) {
          if (i === 0) payment = { amountCents: paymentInfo.amountCents, paymentNote: note };
        } else {
          payment = { amountCents: paymentInfo.perGameCents, paymentNote: note };
        }
        try {
          await registerForGame(gameId, {
            email: email.trim().toLowerCase(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            role,
            preferredPosition: role === "SKATER" && preferredPosition ? preferredPosition : undefined,
            payment,
            signupGroupId,
            finalize: i === selectedGameIds.length - 1,
          });
          registeredCount++;
        } catch (err) {
          const msg = err instanceof Error ? err.message.toLowerCase() : "";
          if (msg.includes("already registered")) {
            registeredCount++;
          } else {
            setSubmitState((s) => ({ ...s, error: "Registration failed. Please try again or contact the organizer." }));
            setPaymentReporting(false);
            return;
          }
        }
      }

      setPaymentReporting(false);
      if (registeredCount === 0) {
        setSubmitState((s) => ({ ...s, error: "Registration failed. Please try again or contact the organizer." }));
        return;
      }

      updateMyProfile({ firstName: firstName.trim(), lastName: lastName.trim(), phone: phone }).catch(() => {});
      setPaymentInfo(null);
      fireConfetti();
      setSuccessModalOpen(true);
    };

    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Stack spacing={3}>
          <Stack spacing={0.5}>
            <Typography variant="h4" component="h3">One last step</Typography>
            <Typography color="text.secondary">
              Complete your payment to finish signing up for <strong>{league.name}</strong>.
            </Typography>
          </Stack>

          {paymentInfo.warningMsg && <Alert severity="warning">{paymentInfo.warningMsg}</Alert>}

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">Complete your payment</Typography>
                <Typography variant="body2" color="text.secondary">
                  {paymentInfo.isFullLeague
                    ? "Full league registration fee"
                    : `${paymentInfo.successCount} game${paymentInfo.successCount !== 1 ? "s" : ""} × ${formatCost(paymentInfo.perGameCents)} per game`}
                </Typography>
                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                  <Typography variant="h5" fontWeight={700}>${amountDollars}</Typography>
                  <Typography variant="body2" color="text.secondary">due</Typography>
                </Stack>

                {paymentInfo.method === "venmo" && paymentInfo.venmoUrl ? (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      fullWidth
                      href={paymentInfo.venmoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setVenmoClicked(true)}
                    >
                      Pay ${amountDollars} via Venmo
                    </Button>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={paymentConfirmed}
                          onChange={(e) => setPaymentConfirmed(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          I&apos;ve sent the Venmo payment of ${amountDollars}
                        </Typography>
                      }
                    />
                    <Button
                      variant="outlined"
                      size="large"
                      fullWidth
                      disabled={!venmoClicked || !paymentConfirmed || paymentReporting}
                      onClick={handleConfirmPayment}
                    >
                      {paymentReporting ? "Confirming…" : "Confirm & finish"}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                      Your payment will show as pending until the league organizer confirms they received it.
                    </Typography>
                  </>
                ) : (
                  <Alert severity="info">
                    Your organizer will reach out with payment instructions.
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    );
  }

  const isFullLeague = league.gamePaymentType === "FULL_LEAGUE";
  const alreadyInAllGames = isFullLeague && games.length > 0 && games.every((g) => !!g.currentUserRole);
  const hasPendingFullLeaguePayment = isFullLeague && games.some((g) => g.currentUserRole && g.currentUserHasPaid === false);
  const pageCount = Math.ceil(games.length / gamesPerPage);
  const pagedGames = games.slice((gamePage - 1) * gamesPerPage, gamePage * gamesPerPage);
  const skillLabel = SKILL_LEVEL_LABELS[league.skillLevel] ?? league.skillLevel;
  const playerCost = formatCost(league.playerCost);
  const goalieCost = formatCost(league.goalieCost);
  const isOrganizerCovered = league.payoutMethod === "none";
  const costLabel = isFullLeague ? "Total league price" : "Per game (drop-in)";
  const cityState = league.city && league.state ? `${league.city}, ${league.state}` : "";

  const handleAuthLogin = () => {
    setAuthPromptOpen(false);
    setLoginOpen(true);
  };

  const handleLoginSuccess = () => {
    setFetchReady(true);
    setAuthPromptOpen(false);
    window.dispatchEvent(new Event("puck-pickup-auth-change"));
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <LoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSuccess={handleLoginSuccess}
      />

      <Dialog
        open={authPromptOpen}
        onClose={() => { setAuthPromptOpen(false); setFetchReady(true); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", textAlign: "center", margin: "0 auto", alignItems: "center", gap: 1 }}>
          <LockOutlinedIcon color="primary" />
          Create an account to join
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ textAlign: "center" }} color="text.secondary" gutterBottom>
            You'll need a free Puck-Pickup account to sign up for{" "}
            <strong>{league?.name ?? "this league"}</strong>. It only takes a minute!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: "column", gap: 1 }}>
          <Button variant="contained" fullWidth onClick={handleAuthLogin}>
            Create account / Sign in
          </Button>
          <Button variant="text" fullWidth onClick={() => { setAuthPromptOpen(false); setFetchReady(true); }}>
            Browse without signing in
          </Button>
        </DialogActions>
      </Dialog>

      <Stack spacing={3}>
        <Stack spacing={0.5}>
          <Typography variant="h4" component="h3">{league.name}</Typography>
          {cityState && <Typography color="text.secondary">{cityState}</Typography>}
        </Stack>

        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap">
              {skillLabel && (
                <Box sx={{ flex: 1, minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Skill level</Typography>
                  <Typography variant="body2">{skillLabel}</Typography>
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="caption" color="text.secondary" display="block">Player cost</Typography>
                <Typography variant="body2">{isOrganizerCovered ? "Covered by organizer" : playerCost}</Typography>
                {!isOrganizerCovered && <Typography variant="caption" color="text.secondary">{costLabel}</Typography>}
              </Box>
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="caption" color="text.secondary" display="block">Goalie cost</Typography>
                <Typography variant="body2">{isOrganizerCovered ? "Covered by organizer" : goalieCost}</Typography>
              </Box>
              {league.maxSkaterSpots != null && (
                <Box sx={{ flex: 1, minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Max skaters per game</Typography>
                  <Typography variant="body2">{league.maxSkaterSpots}</Typography>
                </Box>
              )}
              {league.maxGoalieSpots != null && (
                <Box sx={{ flex: 1, minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Max goalies per game</Typography>
                  <Typography variant="body2">{league.maxGoalieSpots}</Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">
                    {isFullLeague ? "All league games are included in your registration" : "Select games to join"}
                  </Typography>

                  {games.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No upcoming games are scheduled for this league yet.
                    </Typography>
                  ) : (() => {
                    const eligibleGames = games.filter((g) => !g.currentUserRole);
                    const existingReg = games.find((g) => g.currentUserRole);
                    const alreadyInFullLeague = isFullLeague && !!existingReg;
                    const allDropInGamesSignedUp = !isFullLeague && games.length > 0 && eligibleGames.length === 0;
                    const existingRoleLabel = existingReg?.currentUserRole === "GOALIE" ? "Goalie" : "Skater";

                    const renderGameCard = (game) => {
                      const rinkName = game.arenaName || game.rink?.name || "Game";
                      const shortDate = new Date(game.startsAt).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
                      const maxSkater = league.maxSkaterSpots ?? 0;
                      const maxGoalie = league.maxGoalieSpots ?? 0;
                      const skaterFilled = game.skaterSpotsFilled ?? 0;
                      const goalieFilled = game.goalieSpotsFilled ?? 0;
                      const skatersLeft = Math.max(0, maxSkater - skaterFilled);
                      const goaliesLeft = Math.max(0, maxGoalie - goalieFilled);
                      // Per-game "already signed up" only shown for DROP_IN
                      const alreadySignedUp = !isFullLeague && !!game.currentUserRole;
                      const roleLabel = game.currentUserRole === "GOALIE" ? "Goalie" : "Skater";
                      const isRegisteredForGame = !!game.currentUserRole;
                      return (
                        <Card key={game.id} variant="outlined" sx={{ borderRadius: 2, opacity: alreadySignedUp ? 0.75 : 1 }}>
                          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Stack spacing={0.75}>
                              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                {!isFullLeague && (
                                  <Checkbox
                                    checked={!alreadySignedUp && selectedGameIds.includes(game.id)}
                                    onChange={() => !alreadySignedUp && toggleGame(game.id)}
                                    disabled={alreadySignedUp}
                                    size="small"
                                    sx={{ p: 0 }}
                                  />
                                )}
                                <Typography variant="body1" fontWeight={600}>{rinkName}</Typography>
                                {alreadySignedUp && (
                                  <Chip
                                    icon={<CheckCircleIcon />}
                                    label={`You're already signed up · ${roleLabel}`}
                                    color="success"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                {isFullLeague && isRegisteredForGame && (
                                  game.currentUserHasPaid === true
                                    ? <Chip label="Paid" color="success" size="small" variant="outlined" />
                                    : game.currentUserHasPaid === false
                                    ? <Chip label="Payment pending" color="warning" size="small" variant="outlined" />
                                    : null
                                )}
                                <DirectionsChip address={game.rink?.address} />
                              </Stack>
                              <Typography variant="body2" color="text.secondary">
                                {shortDate} · {formatGameTime(game.startsAt)}{game.durationMinutes ? ` · ${game.durationMinutes} min` : ""}
                              </Typography>
                              {(maxSkater > 0 || maxGoalie > 0) && (
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                  <Stack direction="row">
                                    {maxSkater > 0 && (
                                      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                                        <Typography variant="body2">Skaters</Typography>
                                        {skatersLeft === 0
                                          ? <Chip label="Full" color="error" size="small" />
                                          : <Chip label={`${skatersLeft} open`} color="success" size="small" />}
                                      </Stack>
                                    )}
                                    {maxGoalie > 0 && (
                                      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                                        <Typography variant="body2">Goalies</Typography>
                                        {goaliesLeft === 0
                                          ? <Chip label="Full" color="error" size="small" />
                                          : <Chip label={`${goaliesLeft} open`} color="warning" size="small" />}
                                      </Stack>
                                    )}
                                  </Stack>
                                  <Stack direction="row" spacing={2}>
                                    {maxSkater > 0 && (
                                      <Box sx={{ flex: 1 }}>
                                        <LinearProgress
                                          variant="determinate"
                                          value={Math.min(100, (skaterFilled / maxSkater) * 100)}
                                          color={skatersLeft === 0 ? "error" : "success"}
                                          sx={{ height: 4, borderRadius: 1, mb: 0.25 }}
                                        />
                                        <Typography variant="caption" color="text.secondary">{skaterFilled} / {maxSkater}</Typography>
                                      </Box>
                                    )}
                                    {maxGoalie > 0 && (
                                      <Box sx={{ flex: 1 }}>
                                        <LinearProgress
                                          variant="determinate"
                                          value={Math.min(100, (goalieFilled / maxGoalie) * 100)}
                                          color={goaliesLeft === 0 ? "error" : "warning"}
                                          sx={{ height: 4, borderRadius: 1, mb: 0.25 }}
                                        />
                                        <Typography variant="caption" color="text.secondary">{goalieFilled} / {maxGoalie}</Typography>
                                      </Box>
                                    )}
                                  </Stack>
                                </Stack>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    };

                    return (
                      <Stack spacing={1.5}>
                        {/* Top row: Select all (DROP_IN) or already-signed-up banner (FULL_LEAGUE) */}
                        {isFullLeague ? (
                          alreadyInFullLeague && (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label={`You're already signed up for all games · ${existingRoleLabel}`}
                              color="success"
                              variant="outlined"
                              sx={{ alignSelf: "flex-start" }}
                            />
                          )
                        ) : (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={eligibleGames.length > 0 && eligibleGames.every((g) => selectedGameIds.includes(g.id))}
                                indeterminate={eligibleGames.some((g) => selectedGameIds.includes(g.id)) && !eligibleGames.every((g) => selectedGameIds.includes(g.id))}
                                onChange={(e) => setSelectedGameIds(e.target.checked ? eligibleGames.map((g) => g.id) : [])}
                                disabled={eligibleGames.length === 0}
                              />
                            }
                            label={
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="body2" fontWeight={600}>Select all</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  ({selectedGameIds.length} of {eligibleGames.length} selected)
                                </Typography>
                              </Stack>
                            }
                          />
                        )}
                        {allDropInGamesSignedUp && (
                          <Alert severity="success" icon={<CheckCircleIcon />}>
                            You're already signed up for all games in this league.
                          </Alert>
                        )}
                        {pagedGames.map(renderGameCard)}
                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ pt: 2 }}>
                          <Pagination count={pageCount} page={gamePage} onChange={(_, p) => setGamePage(p)} size="small" siblingCount={1} />
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="caption" color="text.secondary">Per page:</Typography>
                            <Select
                              size="small"
                              value={gamesPerPage}
                              onChange={(e) => { setGamesPerPage(e.target.value); setGamePage(1); }}
                              sx={{ fontSize: "0.8rem", height: 28 }}
                            >
                              <MenuItem value={5}>5</MenuItem>
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={25}>25</MenuItem>
                              <MenuItem value={games.length}>All</MenuItem>
                            </Select>
                          </Stack>
                        </Stack>
                      </Stack>
                    );
                  })()}
                </Stack>
              </CardContent>
            </Card>

            {!hasLocallyValidSession() && (
              <Alert
                severity="info"
                variant="outlined"
                action={
                  <Button color="inherit" size="small" onClick={() => setLoginOpen(true)}>
                    Sign in
                  </Button>
                }
              >
                Sign in with a social account to register for this league and sign waivers.
              </Alert>
            )}

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Your information</Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      fullWidth
                      disabled={profileLocked || !hasLocallyValidSession()}
                    />
                    <TextField
                      label="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      fullWidth
                      disabled={profileLocked || !hasLocallyValidSession()}
                    />
                  </Stack>
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => checkWaiverSignatures(email.trim().toLowerCase())}
                    required
                    fullWidth
                    disabled={profileLocked || !hasLocallyValidSession()}
                  />
                  {profileLocked && (
                    <Typography variant="caption" color="text.secondary">
                      See anything off?{" "}
                      <Typography
                        component="button"
                        type="button"
                        variant="caption"
                        color="primary"
                        onClick={handleOpenProfileModal}
                        sx={{ textDecoration: "underline", cursor: "pointer", background: "none", border: "none", p: 0, font: "inherit", display: "inline-flex", alignItems: "center", gap: 0.4 }}
                      >
                        <EditIcon sx={{ fontSize: "0.875rem" }} />
                        Update your profile
                      </Typography>
                    </Typography>
                  )}
                  <PhoneInput
                    value={phone}
                    onChange={setPhone}
                    disabled={phoneLockedFromProfile || !hasLocallyValidSession()}
                  />
                  <FormControl disabled={!hasLocallyValidSession()}>
                    <FormLabel>I am a…</FormLabel>
                    <RadioGroup row value={role} onChange={(e) => setRole(e.target.value)}>
                      <FormControlLabel value="SKATER" control={<Radio />} label="Skater" />
                      <FormControlLabel value="GOALIE" control={<Radio />} label="Goalie" />
                    </RadioGroup>
                  </FormControl>
                  {role === "SKATER" && (
                    <TextField
                      select
                      label="Preferred position (optional)"
                      value={preferredPosition}
                      onChange={(e) => setPreferredPosition(e.target.value)}
                      fullWidth
                      disabled={!hasLocallyValidSession()}
                    >
                      <MenuItem value="">No preference</MenuItem>
                      <MenuItem value="CENTER">Center</MenuItem>
                      <MenuItem value="WING">Wing</MenuItem>
                      <MenuItem value="DEFENSE">Defense</MenuItem>
                    </TextField>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {waivers.length > 0 && (
              <Card variant="outlined" sx={{ borderColor: unsignedWaivers.length > 0 ? "warning.main" : "success.main" }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">Required waiver{waivers.length !== 1 ? "s" : ""}</Typography>
                      {unsignedWaivers.length === 0 && (
                        <Chip icon={<CheckCircleIcon />} label="All signed" color="success" size="small" />
                      )}
                    </Stack>
                    {unsignedWaivers.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Please read and sign the waiver{waivers.length !== 1 ? "s" : ""} below before registering.
                      </Typography>
                    )}
                    {waivers.map((w) => {
                      const signed = waiverSignedIds.has(w.id);
                      return (
                        <Stack key={w.id} direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                            {signed
                              ? <CheckCircleIcon color="success" fontSize="small" sx={{ flexShrink: 0 }} />
                              : <Box sx={{ width: 20, height: 20, borderRadius: "50%", border: 2, borderColor: "warning.main", flexShrink: 0 }} />
                            }
                            <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {w.name}
                            </Typography>
                            {signed && (
                              <Chip label="Signed" color="success" size="small" variant="outlined" />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                            {signed && (
                              <Button
                                size="small"
                                variant="text"
                                color="error"
                                onClick={() => handleResetWaiver(w.id)}
                                disabled={resetingWaiverIds.has(w.id)}
                              >
                                {resetingWaiverIds.has(w.id) ? "Resetting…" : "Re-sign"}
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant={signed ? "text" : "contained"}
                              color={signed ? "inherit" : "warning"}
                              onClick={() => handleOpenWaiverDialog(w)}
                              disabled={!signed && (!firstName.trim() || !lastName.trim() || !email.trim() || !hasLocallyValidSession())}
                            >
                              {signed ? "Review" : "Read & Sign"}
                            </Button>
                          </Stack>
                        </Stack>
                      );
                    })}
                    {!hasLocallyValidSession() ? (
                      <Typography variant="caption" color="text.secondary">
                        Sign in to sign waivers.
                      </Typography>
                    ) : (!firstName.trim() || !lastName.trim() || !email.trim()) ? (
                      <Typography variant="caption" color="text.secondary">
                        Fill in your information above before signing.
                      </Typography>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {submitState.error && (
              <Alert severity="error" onClose={() => setSubmitState((s) => ({ ...s, error: "" }))}>
                {submitState.error}
              </Alert>
            )}

            <Stack direction="row" spacing={2}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitState.loading || games.length === 0 || unsignedWaivers.length > 0 || alreadyInAllGames || !hasLocallyValidSession()}
              >
                {submitState.loading
                  ? "Registering…"
                  : unsignedWaivers.length > 0
                  ? `Sign ${unsignedWaivers.length} waiver${unsignedWaivers.length !== 1 ? "s" : ""} first`
                  : hasPendingFullLeaguePayment
                  ? "Payment Pending"
                  : alreadyInAllGames
                  ? "Already Registered"
                  : isFullLeague
                  ? `Join League (${games.length} game${games.length !== 1 ? "s" : ""})`
                  : `Sign Up for ${selectedGameIds.length} Game${selectedGameIds.length !== 1 ? "s" : ""}`}
              </Button>
              <Button variant="outlined" onClick={() => navigate("/search")}>
                Back to search
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>

      {/* Edit profile modal */}
      <Dialog open={profileModalOpen} onClose={() => !profileModalSaving && setProfileModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update your information</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {profileModalError && (
              <Alert severity="error" onClose={() => setProfileModalError("")}>{profileModalError}</Alert>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="First name"
                value={profileModalForm.firstName}
                onChange={(e) => setProfileModalForm((f) => ({ ...f, firstName: e.target.value }))}
                fullWidth
                disabled={profileModalSaving}
              />
              <TextField
                label="Last name"
                value={profileModalForm.lastName}
                onChange={(e) => setProfileModalForm((f) => ({ ...f, lastName: e.target.value }))}
                fullWidth
                disabled={profileModalSaving}
              />
            </Stack>
            <TextField
              label="Email"
              type="email"
              value={profileModalForm.email}
              onChange={(e) => setProfileModalForm((f) => ({ ...f, email: e.target.value }))}
              fullWidth
              disabled={profileModalSaving}
            />
            <PhoneInput
              value={profileModalForm.phone}
              onChange={(v) => setProfileModalForm((f) => ({ ...f, phone: v }))}
              disabled={profileModalSaving}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setProfileModalOpen(false)} disabled={profileModalSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveProfile} disabled={profileModalSaving}>
            {profileModalSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Registration success modal */}
      <Dialog open={successModalOpen} maxWidth="xs" fullWidth>
        <DialogContent>
          <Stack spacing={2} alignItems="center" sx={{ textAlign: "center", py: 2 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 64 }} />
            <Typography variant="h5" component="h3">You&apos;re registered!</Typography>
            <Typography color="text.secondary">
              You&apos;re all set for <strong>{league?.name}</strong>. See you on the ice!
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, flexDirection: "column", gap: 1 }}>
          <Button variant="contained" fullWidth onClick={() => navigate("/my-schedule")}>
            View my schedule
          </Button>
          <Button variant="text" fullWidth onClick={() => setSuccessModalOpen(false)}>
            Stay on this page
          </Button>
        </DialogActions>
      </Dialog>

      {/* Waiver signing dialog */}
      <Dialog open={waiverDialogOpen} onClose={handleCloseWaiverDialog} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>
          <Typography variant="h6" component="span" display="block">{waiverDialogWaiver?.name ?? "Waiver"}</Typography>
          {waiverDialogWaiver?.version && (
            <Typography variant="caption" component="span" display="block" color="text.secondary">Version {waiverDialogWaiver.version}</Typography>
          )}
        </DialogTitle>
        <DialogContent dividers ref={waiverDialogContentRef}>
          {/* Waiver text */}
          {waiverDialogLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : waiverDialogContent ? (
            <Box
              sx={{
                fontSize: "0.875rem",
                lineHeight: 1.7,
                "& h1,& h2,& h3": { mt: 2, mb: 0.75, fontWeight: 700 },
                "& p": { mt: 0, mb: 1.25 },
                "& ul,& ol": { pl: 3, mb: 1.25 },
              }}
              dangerouslySetInnerHTML={{ __html: waiverDialogContent }}
            />
          ) : (
            <Typography color="text.secondary" variant="body2">Waiver content unavailable.</Typography>
          )}

          {/* Signature section */}
          <Stack spacing={2} sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
            {!waiverSignedIds.has(waiverDialogWaiver?.id) && (
              <Tabs
                value={waiverDialogTab}
                onChange={(_, v) => {
                  setWaiverDialogTab(v);
                  setWaiverDialogHasDrawn(false);
                  setTimeout(() => {
                    if (waiverDialogContentRef.current) {
                      waiverDialogContentRef.current.scrollTop = waiverDialogContentRef.current.scrollHeight;
                    }
                  }, 0);
                }}
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                <Tab icon={<KeyboardIcon fontSize="small" />} iconPosition="start" label="Type name" disabled={waiverDialogSubmitting} />
                <Tab icon={<DrawIcon fontSize="small" />} iconPosition="start" label="Draw signature" disabled={waiverDialogSubmitting} />
              </Tabs>
            )}
            {waiverDialogTab === 0 && (
              <TextField
                label="Type your full legal name to sign"
                value={waiverDialogFullName}
                onChange={(e) => setWaiverDialogFullName(e.target.value)}
                fullWidth
                disabled={waiverDialogSubmitting || waiverSignedIds.has(waiverDialogWaiver?.id)}
                helperText="Your typed name serves as your electronic signature"
                InputProps={{ sx: { fontSize: "1.15rem", py: 1.5 } }}
              />
            )}
            {waiverDialogTab === 1 && (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Sign your name in the box below using your mouse or finger.
                </Typography>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                    bgcolor: isDark ? "grey.900" : "grey.50",
                    height: 180,
                    position: "relative",
                  }}
                >
                  <canvas
                    ref={waiverDialogCanvasRef}
                    style={{ width: "100%", height: "100%", display: "block", touchAction: "none" }}
                  />
                  {!waiverDialogHasDrawn && (
                    <Box
                      sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}
                    >
                      <Typography variant="body2" color="text.disabled">Sign here</Typography>
                    </Box>
                  )}
                </Box>
                <Button type="button" size="small" variant="text" color="inherit" onClick={handleClearWaiverDialogSignature} sx={{ alignSelf: "flex-end" }}>
                  Clear
                </Button>
              </Stack>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={waiverDialogAccepted}
                  onChange={(e) => setWaiverDialogAccepted(e.target.checked)}
                  disabled={waiverDialogSubmitting || waiverSignedIds.has(waiverDialogWaiver?.id)}
                />
              }
              label={
                <Typography variant="body2">
                  I have read the entire waiver above and agree to its terms
                </Typography>
              }
            />
            {waiverDialogError && (
              <Alert severity="error" onClose={() => setWaiverDialogError("")}>
                {waiverDialogError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseWaiverDialog} disabled={waiverDialogSubmitting}>
            {waiverSignedIds.has(waiverDialogWaiver?.id) ? "Close" : "Cancel"}
          </Button>
          {!waiverSignedIds.has(waiverDialogWaiver?.id) && (
            <Button
              variant="contained"
              onClick={handleSignWaiver}
              disabled={
                waiverDialogSubmitting ||
                !waiverDialogAccepted ||
                (waiverDialogTab === 0 && !waiverDialogFullName.trim()) ||
                (waiverDialogTab === 1 && !waiverDialogHasDrawn) ||
                waiverDialogLoading
              }
            >
              {waiverDialogSubmitting ? "Signing…" : "Sign Waiver"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}
